/**
 * Layout of ONE day for the phone's day schedule. Pure functions over `CalendarEvent`s (floating
 * local wall-clock values), no React.
 *
 * - a date-only deadline (or any date-only item) is NOT an event at some hour: it belongs to the
 *   "all day" list;
 * - only items with a real time are placed on the hour axis: a deadline with a time as a short
 *   marker at that time, a timed interval as a block from its start to its end (clipped to the
 *   day);
 * - overlapping items share the width in lanes.
 */
import { addDays } from '@/shared/lib/calendar';

import { type CalendarEvent, splitLocal } from './model';

export const MINUTES_PER_DAY = 24 * 60;
/** Visual length of a point in time (a deadline with a time) and the shortest block. */
export const MIN_BLOCK_MINUTES = 45;

export interface TimedItem {
  event: CalendarEvent;
  /** Minutes from the day's midnight, `0 <= start < end <= 1440`. */
  start: number;
  end: number;
  /** A deadline (a point in time), not an interval. */
  point: boolean;
  lane: number;
  lanes: number;
}

function minutesOf(time: string): number {
  const [hours = 0, minutes = 0] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

/** The last day (inclusive) an event occupies. */
function lastDay(event: CalendarEvent): string {
  const first = event.start.slice(0, 10);
  if (event.kind === 'deadline' || event.end === null) return first;
  if (event.allDay) return addDays(event.end, -1);
  const { date, time } = splitLocal(event.end);
  const last = time === '00:00' ? addDays(date, -1) : date;
  return last < first ? first : last;
}

function touches(event: CalendarEvent, day: string): boolean {
  return event.start.slice(0, 10) <= day && day <= lastDay(event);
}

/** Date-only items of the day: due-date tasks and all-day / multi-day periods. */
export function allDayOf(events: readonly CalendarEvent[], day: string): CalendarEvent[] {
  return events
    .filter((event) => event.allDay && touches(event, day))
    .toSorted((a, b) => a.start.localeCompare(b.start) || a.id.localeCompare(b.id));
}

/** Items with a real time on the day, positioned in lanes. */
export function timedOf(events: readonly CalendarEvent[], day: string): TimedItem[] {
  const items: Omit<TimedItem, 'lane' | 'lanes'>[] = [];
  for (const event of events) {
    if (event.allDay || !touches(event, day)) continue;
    const startsToday = event.start.slice(0, 10) === day;
    const start = startsToday ? minutesOf(event.start.slice(11, 16)) : 0;
    if (event.kind === 'deadline' || event.end === null) {
      items.push({
        event,
        start,
        end: Math.min(MINUTES_PER_DAY, start + MIN_BLOCK_MINUTES),
        point: true,
      });
      continue;
    }
    const { date, time } = splitLocal(event.end);
    const end = date > day ? MINUTES_PER_DAY : date === day ? minutesOf(time) : start;
    items.push({
      event,
      start,
      end: Math.min(MINUTES_PER_DAY, Math.max(end, start + MIN_BLOCK_MINUTES)),
      point: false,
    });
  }
  items.sort((a, b) => a.start - b.start || a.end - b.end || a.event.id.localeCompare(b.event.id));
  return assignLanes(items);
}

/** Greedy lanes; every item of an overlapping cluster gets the cluster's lane count. */
function assignLanes(items: Omit<TimedItem, 'lane' | 'lanes'>[]): TimedItem[] {
  const result: TimedItem[] = [];
  let cluster: TimedItem[] = [];
  let clusterEnd = 0;
  let laneEnds: number[] = [];
  const flush = () => {
    for (const item of cluster) item.lanes = laneEnds.length;
    cluster = [];
    laneEnds = [];
  };
  for (const base of items) {
    if (cluster.length > 0 && base.start >= clusterEnd) flush();
    let lane = laneEnds.findIndex((end) => end <= base.start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(base.end);
    } else {
      laneEnds[lane] = base.end;
    }
    const item: TimedItem = { ...base, lane, lanes: 1 };
    cluster.push(item);
    result.push(item);
    clusterEnd = Math.max(clusterEnd, base.end);
  }
  flush();
  return result;
}

/** The first hour worth showing on opening: the earliest item, else the working day's start. */
export function firstVisibleHour(items: readonly TimedItem[], fallback = 8): number {
  const first = items[0];
  return first ? Math.max(0, Math.floor(first.start / 60) - 1) : fallback;
}

/** Whether any item (timed or all-day) touches the day: the dot in the date strip. */
export function hasItems(events: readonly CalendarEvent[], day: string): boolean {
  return events.some((event) => touches(event, day));
}
